import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import CMSRender from "aldoo-ra/CMS/cms-render"
import CMSData from "aldoo-ra/CMS/cms-data"
import { showNotification } from "aldoo-ra/Notification"
// Import SubmitSupportTicket directly
import { SubmitSupportTicket } from "aldoo-ra/Admin/CustomerSupport/api"
import PubSub from "pubsub-js"
import Localization from "aldoo-ra/Localization"
import emailValidator from "email-validator"

const ContactPanel = () => {
  // Add a ref to the file input
  const { tjsx, currentLanguage } = Localization()
  const fileInputRef = useRef(null)

  const triggerFileUpload = () => {
    fileInputRef.current.click()
  }

  const [questionType, setQuestionType] = useState("")
  const [message, setMessage] = useState("")
  const [email, setEmail] = useState("")
  const [file, setFile] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  // Fetch question type categories from CMS
  const { data: categoryData, loading: categoriesLoading } = CMSData({
    contentType: "CSRequestCategory",
    match: {
      "content.id": "contact-us",
      "content.type": "CSRequestCategory",
    },
    language: currentLanguage,
  })

  // Extract question type options from CMS data
  const questionTypeOptions =
    categoryData && categoryData.length > 0 ? categoryData[0]?.pairs || {} : {}

  useEffect(() => {
    const token = PubSub.subscribe("open-contacts", () => {
      setIsOpen(true)
    })
    return () => {
      PubSub.unsubscribe(token)
    }
  }, [])

  //reset the form when the modal is closed
  useEffect(() => {
    if (!isOpen) {
      setQuestionType("")
      setMessage("")
      setEmail("")
      setFile(null)
      setErrors({})
      setSubmitError(null)
      setIsSubmitting(false)
    }
  }, [isOpen])

  const handleQuestionTypeChange = (e) => {
    setQuestionType(e.target.value)
    // Clear error when user makes a selection
    if (errors.questionType) {
      const newErrors = { ...errors }
      delete newErrors.questionType
      setErrors(newErrors)
    }
  }

  const handleMessageChange = (e) => {
    setMessage(e.target.value)
    // Clear error when user starts typing
    if (errors.message) {
      const newErrors = { ...errors }
      delete newErrors.message
      setErrors(newErrors)
    }
  }

  const handleEmailChange = (e) => {
    setEmail(e.target.value)
    // Clear error when user starts typing
    if (errors.email) {
      const newErrors = { ...errors }
      delete newErrors.email
      setErrors(newErrors)
    }
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    setFile(selectedFile)
    // Clear error when user selects a file
    if (errors.file) {
      const newErrors = { ...errors }
      delete newErrors.file
      setErrors(newErrors)
    }
  }

  const validateForm = () => {
    const errors = {}
    if (!questionType) errors.questionType = `Please select a question type.`
    if (!message || !message.trim()) errors.message = `Please enter a message.`
    
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      errors.email = `Please enter an email.`
    } else {
      // Check if email format is valid using email-validator
      if (!emailValidator.validate(trimmedEmail)) {
        errors.email = `Please enter a valid email address.`
      }
    }

    //if there is a file and it's more than 20MB, show an error
    if (file && file.size > 20 * 1024 * 1024) {
      errors.file = `File size must be less than 20MB.`
    }

    return errors
  }

  const handleSubmit = async () => {
    setSubmitError(null)

    // Explicit email validation check
    const trimmedEmail = email.trim()
    if (!trimmedEmail || !emailValidator.validate(trimmedEmail)) {
      setErrors({ email: !trimmedEmail ? 'Please enter an email.' : 'Please enter a valid email address.' })
      return
    }

    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    setErrors({})

    setIsSubmitting(true)
    try {
      // Convert questionType to tags array
      const tags = questionType ? [questionType] : []

      // Create files array if file exists
      const files = file ? [file] : null

      // Create payload in the format expected by SubmitSupportTicket
      const payload = {
        email: email.trim(),
        tags, // Use tags array instead of questionType field
        description: message.trim(), // Use message as description
        files, // Use files array
      }

      // Use SubmitSupportTicket directly
      const response = await SubmitSupportTicket(payload)

      if (response.error) {
        // Handle specific error codes
        const newErrors = {}

        if (response.error === "no_email") {
          newErrors.email = "Email is required."
        } else if (response.error === "no_tags") {
          newErrors.questionType = "Question type selection is required."
        } else if (response.error === "no_description") {
          newErrors.message = "Message is required."
        } else if (response.error === "file_too_big") {
          newErrors.file = "File size must be less than 20MB."
        } else if (response.error === "email_send_failed") {
          setSubmitError("Failed to send email. Please try again later.")
        } else if (response.error === "protection_error") {
          showNotification({
            message:
              "Message Protection error. Please revise your message or email",
            className: "bg-error text-admin_text_inv",
          })
        } else {
          setSubmitError(`Server error: ${response.error}`)
        }

        // Only set errors if we have any
        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors)
        }
        return
      }

      if (response.result) {
        showNotification({
          message: "Thank you! We'll review your request immediately.",
          className: "bg-primary text-admin_text_inv",
        })

        // Close the modal on successful submission
        setIsOpen(false)
      }
    } catch (error) {
      console.error("Request submission error:", error)
      setSubmitError(
        "An error occurred while submitting your request. Please try again."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <CMSRender
      contentType="ContactPanel"
      renderItem={(dialog) => {
        return (
          <Modal
            isOpen={isOpen}
            onClose={() => {
              setIsOpen(false)
            }}
            variant="mobilingo_big"
            className="sm:w-[500px] md:w-[670px] h-full md:h-auto"
            zIndex={410}
            title={dialog.panel_title}
          >
            <div className="w-full px-5">
              <form onSubmit={(e) => e.preventDefault()}>
                {/* Question Type and Select dropdown */}
                <div className="mb-4">
                  <label
                    className={`block mb-2 ${
                      errors.questionType ? "text-error" : "text-text"
                    }`}
                  >
                    <Typography
                      variant="body1_accent"
                      className="text-text my-0"
                    >
                      {dialog.question_type_label}
                    </Typography>
                  </label>

                  <Typography variant="txt1_dim" className="relative">
                    <select
                      value={questionType}
                      onChange={handleQuestionTypeChange}
                      className={`appearance-none bg-gray_100 border rounded-full ${
                        errors.questionType ? "border-error" : "border-bordered"
                      } rounded focus:outline-none focus:border-none focus:ring-2 focus:ring-primary px-5 pr-10 py-2 w-full`}
                      disabled={categoriesLoading}
                    >
                      <option value="">
                        {categoriesLoading
                          ? "Loading options..."
                          : "Select an option"}
                      </option>
                      {/* Dynamic options from CMS */}
                      {Object.entries(questionTypeOptions).map(
                        ([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        )
                      )}
                    </select>

                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                      <svg
                        className="fill-current h-4 w-4 text-text"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </Typography>

                  {errors.questionType && (
                    <p className="text-error text-sm mt-2">
                      {errors.questionType}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label
                    className={`block mb-2 ${
                      errors.email ? "text-error" : "text-text"
                    }`}
                  >
                    <Typography
                      variant="body1_accent"
                      className="text-text my-0"
                    >
                      {dialog.email_label}
                    </Typography>
                  </label>

                  <input
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    className={`outline-none text-text bg-gray_100 border ${
                      errors.email ? "border-error" : "border-bordered"
                    } rounded-full focus:outline-none focus:border-none focus:ring-2 focus:ring-primary p-2 px-5 w-full`}
                  />

                  {errors.email && (
                    <p className="text-error text-sm mt-2">{errors.email}</p>
                  )}
                </div>

                {/* Message */}
                <div className="mb-4">
                  <label
                    className={`block mb-2 ${
                      errors.message ? "text-error" : "text-text"
                    }`}
                  >
                    <Typography
                      variant="body1_accent"
                      className="text-text my-0"
                    >
                      {dialog.message_label}
                    </Typography>
                  </label>

                  <textarea
                    value={message}
                    onChange={handleMessageChange}
                    className={`outline-none text-text bg-gray_100 border ${
                      errors.message ? "border-error" : "border-bordered"
                    } rounded-[16px] focus:outline-none focus:border-none focus:ring-2 focus:ring-primary p-2 w-full`}
                    rows="4"
                  ></textarea>

                  {errors.message && (
                    <p className="text-error text-sm mt-2">{errors.message}</p>
                  )}
                </div>

                {/* File Attachment */}
                <div className="flex items-center gap-2 mb-5">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }} // Hide the actual input
                  />

                  {/* Attach File Button */}
                  <Button
                    type="button"
                    variant="secondary_s"
                    onClick={triggerFileUpload}
                    className="flex gap-2"
                  >
                    {/* Attach */}
                    <img src={dialog.add_file_btn?.[0]?.image} alt="Add file" />

                    {dialog.add_file_btn?.[0]?.label}
                  </Button>

                  {/* File Status Text */}
                  <Typography
                    variant="txt2_dim"
                    className="text-admin_text_secondary"
                  >
                    {file
                      ? `Selected file: ${file.name}`
                      : dialog.no_file_attached}
                  </Typography>

                  {errors.file && (
                    <p className="text-error text-sm mt-2">{errors.file}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="mb-4">
                  <Button
                    type="button"
                    variant="admin_primary"
                    className="w-full"
                    disabled={isSubmitting || categoriesLoading}
                    onClick={handleSubmit}
                  >
                    {isSubmitting
                      ? `Sending...`
                      : categoriesLoading
                      ? `Loading...`
                      : dialog.submit_btn?.[0]?.label}
                  </Button>
                  {Object.keys(errors).length > 0 && (
                    <p className="text-error mb-4 text-center mt-3">
                      {`Please correct the highlighted fields above.`}
                    </p>
                  )}
                  {submitError && <p className="text-error">{submitError}</p>}
                </div>
              </form>

              <div className="flex items-center justify-center">
                <Typography variant="txt1_dim">
                  {tjsx(dialog.footer_text, {
                    tos: (
                      <a
                        key={"tos"}
                        href={dialog.footer_links?.[0]?.link?.[0]?.url}
                        target="_blank"
                        className="text-primary hover:underline"
                      >
                        {dialog.footer_links?.[0]?.label}
                      </a>
                    ),
                    pp: (
                      <a
                        key={"pp"}
                        href={dialog.footer_links?.[1]?.link?.[0]?.url}
                        target="_blank"
                        className="text-primary hover:underline"
                      >
                        {dialog.footer_links?.[1]?.label}
                      </a>
                    ),
                  })}
                </Typography>
              </div>
            </div>
          </Modal>
        )
      }}
    />
  )
}

export default ContactPanel
